'use client';
import { jsx, jsxs } from 'react/jsx-runtime';
import { motion } from 'framer-motion';
import { useCallback } from 'react';
import styled from 'styled-components';
import { withTitan } from '../../hocs/withTitan/withTitan.js';
import '../../mixins/media.js';
import { keyboardFocusShadowAfter } from '../../mixins/keyboardFocus.js';
import '../../mixins/markStyle.js';
import '../../mixins/scrollbarStyle.js';
import { spacing } from '../../mixins/spacing.js';
import { textStyle } from '../../mixins/textStyle.js';
import { Box } from '../Box/Box.js';
import '../Icons/AlignStartHorizontalIcon.js';
import '../Icons/ArchiveIcon.js';
import '../Icons/ArrowDownIcon.js';
import '../Icons/ArrowLeftBottomIcon.js';
import '../Icons/ArrowLeftIcon.js';
import '../Icons/ArrowLeftTopIcon.js';
import '../Icons/ArrowRightBottomIcon.js';
import '../Icons/ArrowRightIcon.js';
import '../Icons/ArrowRightTopIcon.js';
import '../Icons/BagIcon.js';
import '../Icons/BasketIcon.js';
import '../Icons/BellFilledIcon.js';
import '../Icons/BellNotificationIcon.js';
import '../Icons/BellOutlineIcon.js';
import '../Icons/BinocularsIcon.js';
import '../Icons/BlocksIcon.js';
import '../Icons/BoltIcon.js';
import '../Icons/BookIcon.js';
import '../Icons/BookmarkAddFilledIcon.js';
import '../Icons/BookmarkAddOutlineIcon.js';
import '../Icons/BookmarkIcon.js';
import '../Icons/BookOpenedIcon.js';
import '../Icons/BoxAltIcon.js';
import '../Icons/BusinessIcon.js';
import '../Icons/CalendarIcon.js';
import '../Icons/CancelIcon.js';
import '../Icons/CardsIcon.js';
import '../Icons/ChatAltIcon.js';
import '../Icons/ChatIcon.js';
import '../Icons/CheckboxIcon.js';
import '../Icons/CheckCirlcleFilledIcon.js';
import '../Icons/CheckSquareIcon.js';
import '../Icons/ChevronDoubleLeftIcon.js';
import '../Icons/ChevronDoubleRightIcon.js';
import '../Icons/ChevronDownIcon.js';
import '../Icons/ChevronLeftIcon.js';
import { ChevronRightIcon } from '../Icons/ChevronRightIcon.js';
import '../Icons/ChevronUpIcon.js';
import '../Icons/ClipboardEditIcon.js';
import '../Icons/ClipboardIcon.js';
import '../Icons/CloseIcon.js';
import '../Icons/CollapseIcon.js';
import '../Icons/ControlsIcon.js';
import '../Icons/ControlsVerticalAltIcon.js';
import '../Icons/CopyIcon.js';
import '../Icons/CutIcon.js';
import '../Icons/DangerIcon.js';
import '../Icons/DownloadCloudIcon.js';
import '../Icons/DownloadIcon.js';
import '../Icons/DownloadPdfIcon.js';
import '../Icons/DrawerOpenIcon.js';
import '../Icons/EllipsisIcon.js';
import '../Icons/EllypsisVerticalIcon.js';
import '../Icons/EnvelopeIcon.js';
import '../Icons/ErrorIcon.js';
import '../Icons/ExitIcon.js';
import '../Icons/ExpandIcon.js';
import '../Icons/ExternalLinkIcon.js';
import '../Icons/EyeCrossedIcon.js';
import '../Icons/EyeIcon.js';
import '../Icons/FacebookIcon.js';
import '../Icons/FavouriteIcon.js';
import '../Icons/FileBadgeIcon.js';
import '../Icons/FileIcon.js';
import '../Icons/FileSearchIcon.js';
import '../Icons/FileType2Icon.js';
import '../Icons/FilmIcon.js';
import '../Icons/FitscreenIcon.js';
import '../Icons/FocusIcon.js';
import '../Icons/FolderAddIcon.js';
import '../Icons/FolderClosedIcon.js';
import '../Icons/FolderIcon.js';
import '../Icons/FutureIcon.js';
import '../Icons/GalleryVerticalEndIcon.js';
import '../Icons/GridIcon.js';
import '../Icons/HelpIcon.js';
import '../Icons/HistoryIcon.js';
import '../Icons/HomeIcon.js';
import '../Icons/InformationIcon.js';
import '../Icons/InstagramIcon.js';
import '../Icons/LibraryIcon.js';
import '../Icons/LightbulbIcon.js';
import '../Icons/LinkedinIcon.js';
import '../Icons/ListIcon.js';
import '../Icons/ListSearchIcon.js';
import '../Icons/Loader2Icon.js';
import '../Icons/LockAltIcon.js';
import '../Icons/LockAltOpenIcon.js';
import '../Icons/LockIcon.js';
import '../Icons/LockOpenIcon.js';
import '../Icons/MagazineIcon.js';
import '../Icons/MailIcon.js';
import '../Icons/Maximize2Icon.js';
import '../Icons/MenuCollapsable2Icon.js';
import '../Icons/MenuCollapsableIcon.js';
import '../Icons/MenuDotsIcon.js';
import '../Icons/MenuIcon.js';
import '../Icons/MinusIcon.js';
import '../Icons/NewsIcon.js';
import '../Icons/NtfrIcon.js';
import '../Icons/OkCircleIcon.js';
import '../Icons/OkIcon.js';
import '../Icons/PencilIcon.js';
import '../Icons/PenSquareIcon.js';
import '../Icons/People2Icon.js';
import '../Icons/PeopleIcon.js';
import '../Icons/PersonIcon.js';
import '../Icons/PhoneCallIcon.js';
import '../Icons/PlayFilledIcon.js';
import '../Icons/PlayIcon.js';
import '../Icons/PlusCircleIcon.js';
import '../Icons/PlusIcon.js';
import '../Icons/PlusSquareIcon.js';
import '../Icons/PrintIcon.js';
import '../Icons/RadioIcon.js';
import '../Icons/RefreshCwIcon.js';
import '../Icons/RotateIcon.js';
import '../Icons/SearchIcon.js';
import '../Icons/SettingsIcon.js';
import '../Icons/ShareIcon.js';
import '../Icons/ShieldIcon.js';
import '../Icons/SocialXIcon.js';
import '../Icons/StarFilledIcon.js';
import '../Icons/StarOutlineIcon.js';
import '../Icons/StickyNoteIcon.js';
import '../Icons/SwapHorizontalIcon.js';
import '../Icons/TablePropertiesIcon.js';
import '../Icons/TasklistIcon.js';
import '../Icons/ThumbDownIcon.js';
import '../Icons/ThumbsDownIcon.js';
import '../Icons/ThumbsUpIcon.js';
import '../Icons/ThumbUpIcon.js';
import '../Icons/TimeIcon.js';
import '../Icons/TimerIcon.js';
import '../Icons/Trash2Icon.js';
import '../Icons/TrashIcon.js';
import '../Icons/UpgradeIcon.js';
import '../Icons/UploadIcon.js';
import '../Icons/UserIcon.js';
import '../Icons/VideoIcon.js';
import '../Icons/WorldIcon.js';
import '../Icons/XposiIcon.js';
import '../Icons/YoutubeIcon.js';
import { useTable } from './TableProvider.js';
import { Td } from './Td.js';
import { Tr } from './Tr.js';

const TableGroup = styled(Td).withConfig({
  componentId: "sc-1kym1rg-0"
})(["position:relative;cursor:", ";", ""], ({
  canExpand
}) => canExpand ? 'pointer' : 'normal', keyboardFocusShadowAfter({
  offset: 4
}));
const TableGroupTitle = styled(Box).withConfig({
  componentId: "sc-1kym1rg-1"
})(["color:", ";", ";", ":hover &{text-decoration:underline;}"], ({
  theme
}) => theme.components.table.groupTitleColor, textStyle('paragraphSemiBold'), TableGroup);
const TableGroupDescription = styled(Box).withConfig({
  componentId: "sc-1kym1rg-2"
})(["", ";color:", ";"], textStyle('paragraphSmall'), ({
  theme
}) => theme.components.table.groupDescriptionColor);
const ToggleIconContainer = styled(motion.div).withConfig({
  componentId: "sc-1kym1rg-3"
})(["position:absolute;top:", ";height:20px;margin-left:-24px;display:flex;align-items:center;"], spacing(3));
const TableGroupRow = /*#__PURE__*/withTitan(({
  row
}) => {
  const {
    customGroupHeader,
    labelGroupCollapse,
    labelGroupExpand,
    shouldIndentGrouping
  } = useTable();
  const handleKeydown = useCallback((event, handler) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handler === null || handler === void 0 ? void 0 : handler();
    }
  }, []);
  const {
    title,
    description
  } = typeof customGroupHeader === 'function' ? customGroupHeader(row) : {
    title: row.groupingValue,
    description: undefined
  };
  return jsx(Tr, {
    children: jsxs(TableGroup, {
      "aria-expanded": row.getIsExpanded(),
      "aria-label": row.getIsExpanded() ? labelGroupCollapse : labelGroupExpand,
      canExpand: row.getCanExpand(),
      colSpan: row.getVisibleCells().length,
      groupLevel: shouldIndentGrouping ? row.depth + 1 : undefined,
      hasRowSpan: false,
      tabIndex: 0,
      onClick: row.getToggleExpandedHandler(),
      onKeyDown: e => handleKeydown(e, row.getToggleExpandedHandler()),
      children: [jsx(ToggleIconContainer, {
        "aria-hidden": true,
        animate: row.getIsExpanded() ? 'expanded' : 'collapsed',
        initial: false,
        transition: {
          ease: 'easeInOut',
          duration: 0.3
        },
        variants: {
          collapsed: {
            rotate: 0
          },
          expanded: {
            rotate: '90deg'
          }
        },
        children: jsx(ChevronRightIcon, {})
      }, "right"), jsx(TableGroupTitle, {
        spaceAfter: description ? 1 : 0,
        children: title
      }), description && jsx(TableGroupDescription, {
        children: description
      })]
    })
  }, row.id);
}, {
  name: 'TableBody'
});

export { TableGroupRow };
//# sourceMappingURL=TableGroupRow.js.map
